import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import {
  brokerSusep,
  emptyAddress,
  emptyBankAccount,
  emptyFinancialContact,
  products,
  type Address,
  type BankAccount,
  type Broker,
  type BrokerInput,
  type FinancialContact,
} from '@/shared/api/resources';
import { ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/useAuth';
import { cnpjCompleto } from '@/shared/lib/consultaCnpj';
import { rateInputError, rateToBasisPoints, rateToInput } from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { AvisoDaConsultaCnpj } from '@/shared/ui/AvisoDaConsultaCnpj';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useConsultaCnpj } from '@/shared/ui/useConsultaCnpj';

import { fieldErrorOf } from '../shared/fieldError';

import { AvisoDaSusep } from './AvisoDaSusep';

import { dicaDadosBancarios, permissaoDadosBancarios } from './contaBancaria';
import { FinancialDataFields } from './FinancialDataFields';
import {
  camposDoCorretorDaReceita,
  enderecoDoCorretor,
  type CampoDoCorretor,
} from './receitaDoCorretor';

const situacoes = [
  { value: 'nao_consultado', label: 'Nao consultado' },
  { value: 'regular', label: 'Regular' },
  { value: 'irregular', label: 'Irregular' },
  { value: 'nao_encontrado', label: 'Nao encontrado' },
];

export function BrokerForm({
  broker,
  readOnly,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  broker?: Broker | undefined;
  readOnly?: boolean | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: BrokerInput) => void;
  onCancel: () => void;
}) {
  // Conta e Pix sao o destino do repasse e tem alcada propria. Sem ela, os campos
  // ficam travados no valor gravado, e o formulario os devolve como estao: o
  // servidor compara com o gravado e nao recusa o que nao mudou.
  const { can } = useAuth();
  const podeDadosBancarios = can(permissaoDadosBancarios);
  // A comissao tem alcada propria (P-COR-1): no OnPoint so o ADMIN a define. Sem a
  // permissao, o campo fica travado e nao vai no pedido.
  const podeComissao = can('corretores.comissao.update');

  const [legalName, setLegalName] = useState(broker?.legal_name ?? '');
  const [tradeName, setTradeName] = useState(broker?.trade_name ?? '');
  const [document, setDocument] = useState(broker?.document ?? '');
  const [susep, setSusep] = useState(broker?.susep_code ?? '');
  const [externalStatus, setExternalStatus] = useState(
    broker?.external_status ?? 'nao_consultado',
  );
  const [email, setEmail] = useState(broker?.email ?? '');
  const [phone, setPhone] = useState(broker?.phone ?? '');
  const [contactName, setContactName] = useState(broker?.contact_name ?? '');
  const [commission, setCommission] = useState(
    broker ? rateToInput(broker.commission_bps) : '',
  );
  const [notes, setNotes] = useState(broker?.notes ?? '');
  const [address, setAddress] = useState<Address>(broker?.address ?? emptyAddress);
  const [pixKey, setPixKey] = useState(broker?.pix_key ?? '');
  const [pixKind, setPixKind] = useState(broker?.pix_kind ?? '');
  const [commercialOwner, setCommercialOwner] = useState(
    broker?.commercial_owner ?? '',
  );
  const [productIds, setProductIds] = useState<string[]>(broker?.product_ids ?? []);
  const [municipalRegistration, setMunicipalRegistration] = useState(
    broker?.municipal_registration ?? '',
  );
  const [bankAccount, setBankAccount] = useState<BankAccount>(
    broker?.bank_account ?? emptyBankAccount,
  );
  const [financialContact, setFinancialContact] = useState<FinancialContact>(
    broker?.financial_contact ?? emptyFinancialContact,
  );

  // Consulta de CNPJ pela BrasilAPI (ADR 0092): a corretora de seguros e empresa
  // comum na Receita. Preenche so o vazio e pergunta antes de trocar o digitado.
  const consultaCnpj = useConsultaCnpj<CampoDoCorretor>({
    planejar: (r) =>
      camposDoCorretorDaReceita(
        { legal_name: legalName, trade_name: tradeName, email, phone, address },
        r,
      ),
    aplicar: (chaves, r) => {
      for (const chave of chaves) {
        switch (chave) {
          case 'legal_name':
            setLegalName(r.legal_name);
            break;
          case 'trade_name':
            setTradeName(r.trade_name);
            break;
          case 'email':
            setEmail(r.email);
            break;
          case 'phone':
            setPhone(r.phone);
            break;
          case 'address':
            setAddress(enderecoDoCorretor(r));
            break;
        }
      }
    },
  });

  // A habilitacao por produto so faz sentido com a lista de produtos em maos. Ela
  // vem da mesma consulta que outras telas usam, entao o cache costuma ja te-la.
  const listaProdutos = useQuery({
    queryKey: ['products', 'options'],
    queryFn: ({ signal }) => products.list(signal),
  });

  const apiError = error instanceof ApiError ? error : null;
  const erroComissao = rateInputError(commission);

  // Triagem da SUSEP (ADR 0095): no cadastro novo e na troca de CNPJ, o CNPJ completo e
  // consultado antes de salvar. A corretora que nao esta apta nao e salva; a apta leva o
  // numero do registro. A tela so antecipa: o servidor confere de novo ao gravar.
  const cnpjValido = cnpjCompleto(document);
  const precisaTriagem =
    !readOnly && cnpjValido !== null && (!broker || cnpjValido !== broker.document);
  const triagem = useQuery({
    queryKey: ['broker-susep-check', cnpjValido],
    queryFn: ({ signal }) => brokerSusep.check(cnpjValido ?? '', signal),
    enabled: precisaTriagem,
    retry: false,
    staleTime: 60_000,
  });
  const resultadoSusep = precisaTriagem ? triagem.data : undefined;
  const susepApta = resultadoSusep?.status === 'apta';
  const susepDoFormulario =
    susepApta && resultadoSusep.susep_number ? resultadoSusep.susep_number : susep;
  const situacaoDoFormulario = susepApta ? 'regular' : externalStatus;
  const susepBloqueia =
    precisaTriagem &&
    (triagem.isFetching || (resultadoSusep !== undefined && !resultadoSusep.proceed));

  function submit(event: FormEvent) {
    event.preventDefault();
    if (erroComissao || susepBloqueia) return;
    onSubmit({
      legal_name: legalName,
      trade_name: tradeName,
      document,
      susep_code: susepDoFormulario,
      external_status: situacaoDoFormulario,
      email,
      phone,
      contact_name: contactName,
      ...(podeComissao ? { commission_bps: rateToBasisPoints(commission || '0') } : {}),
      notes,
      address,
      pix_key: pixKey,
      pix_kind: pixKind,
      commercial_owner: commercialOwner,
      product_ids: productIds,
      municipal_registration: municipalRegistration,
      bank_account: bankAccount,
      financial_contact: financialContact,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      <fieldset disabled={readOnly || submitting} className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Razao social"
            required
            value={legalName}
            error={fieldErrorOf(apiError, 'legal_name')}
            onChange={(event) => setLegalName(event.target.value)}
          />
          <Field
            label="Nome fantasia"
            value={tradeName}
            onChange={(event) => setTradeName(event.target.value)}
          />
          <div className="flex flex-col gap-1.5">
            <Field
              label="CNPJ"
              required
              placeholder="00.000.000/0000-00"
              value={document}
              error={fieldErrorOf(apiError, 'document')}
              hint="A mascara e opcional. O cadastro guarda apenas os digitos."
              onChange={(event) => {
                setDocument(event.target.value);
                consultaCnpj.acompanhar(event.target.value);
              }}
            />
            {!readOnly ? (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                loading={consultaCnpj.situacao.tipo === 'consultando'}
                disabled={cnpjCompleto(document) === null}
                onClick={() => consultaCnpj.consultar(document)}
              >
                Buscar dados na Receita
              </Button>
            ) : null}
          </div>
          <Field
            label="Codigo SUSEP"
            value={susepDoFormulario}
            readOnly={susepApta}
            hint={susepApta ? 'Numero do registro na SUSEP.' : undefined}
            onChange={(event) => setSusep(event.target.value)}
          />
          <Field
            label="Inscricao municipal"
            maxLength={30}
            value={municipalRegistration}
            error={fieldErrorOf(apiError, 'municipal_registration')}
            onChange={(event) => setMunicipalRegistration(event.target.value)}
          />
        </div>

        <AvisoDaConsultaCnpj consulta={consultaCnpj} />

        {precisaTriagem && triagem.isFetching ? (
          <p className="text-2xs text-ink-500">Consultando o registro na SUSEP...</p>
        ) : (
          <AvisoDaSusep triagem={resultadoSusep} />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Situacao cadastral externa"
            options={situacoes}
            value={situacaoDoFormulario}
            disabled={susepApta}
            hint={
              susepApta
                ? 'Vem da consulta a SUSEP.'
                : 'Registrada a mao ate haver integracao configurada.'
            }
            onChange={(event) => setExternalStatus(event.target.value)}
          />
          <Field
            label="Comissao (%)"
            inputMode="decimal"
            disabled={!podeComissao}
            value={commission}
            error={erroComissao ?? fieldErrorOf(apiError, 'commission_bps')}
            onChange={(event) => setCommission(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Contato"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
          />
          <Field
            label="Email"
            type="email"
            value={email}
            error={fieldErrorOf(apiError, 'email')}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Telefone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        <Field
          label="Comercial responsavel"
          value={commercialOwner}
          hint="Quem cuida desta conta. Nem todo responsavel tem usuario na plataforma."
          onChange={(event) => setCommercialOwner(event.target.value)}
        />

        <fieldset
          disabled={!podeDadosBancarios}
          className="grid gap-4 sm:grid-cols-2"
          aria-label="Pix de recebimento"
        >
          {!podeDadosBancarios ? (
            <p className="text-2xs text-ink-500 sm:col-span-2">{dicaDadosBancarios}</p>
          ) : null}
          <Select
            label="Forma de recebimento"
            value={pixKind}
            placeholder="Nao informada"
            options={[
              { value: 'cnpj', label: 'Pix por CNPJ' },
              { value: 'cpf', label: 'Pix por CPF' },
              { value: 'email', label: 'Pix por e-mail' },
              { value: 'telefone', label: 'Pix por telefone' },
              { value: 'aleatoria', label: 'Pix por chave aleatoria' },
            ]}
            hint="O tipo acompanha a chave porque a conferencia depende dele."
            onChange={(event) => {
              setPixKind(event.target.value);
              if (event.target.value === '') setPixKey('');
            }}
          />
          <Field
            label="Chave de recebimento"
            value={pixKey}
            disabled={pixKind === ''}
            error={fieldErrorOf(apiError, 'pix_key')}
            onChange={(event) => setPixKey(event.target.value)}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs font-semibold text-ink-700">
            Produtos habilitados
          </legend>
          <p className="text-2xs text-ink-500">
            Sem nenhum marcado, o corretor opera qualquer produto da carteira.
          </p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {(listaProdutos.data ?? []).map((produto) => (
              <label
                key={produto.id}
                className="flex cursor-pointer items-center gap-2 text-xs text-ink-700"
              >
                <input
                  type="checkbox"
                  checked={productIds.includes(produto.id)}
                  onChange={(event) =>
                    setProductIds((atual) =>
                      event.target.checked
                        ? [...atual, produto.id]
                        : atual.filter((item) => item !== produto.id),
                    )
                  }
                  className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                />
                <span className="truncate">{produto.name}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <AddressFields value={address} onChange={setAddress} />

        {/* A conta bancaria e destino do repasse, como a chave Pix, e so quem tem a
            permissao de dados bancarios a troca. Toda troca fica na trilha. */}
        <section className="flex flex-col gap-3 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-ink-900">Dados financeiros</h3>
          <FinancialDataFields
            conta={bankAccount}
            responsavel={financialContact}
            erro={apiError}
            contaSomenteLeitura={!podeDadosBancarios}
            onContaChange={setBankAccount}
            onResponsavelChange={setFinancialContact}
          />
        </section>

        <Textarea
          label="Observacoes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </fieldset>

      {!readOnly ? (
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={submitting}
            disabled={erroComissao !== undefined || susepBloqueia}
          >
            {broker ? 'Salvar alteracoes' : 'Cadastrar'}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
